import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TextInput } from "react-native";

import {
  sendReminderSmsVerificationCode,
  toDigitsOnlyPhoneNumber as toDigitsOnlyApiPhoneNumber,
  verifyReminderSmsVerificationCode,
} from "../services/smsOtpApi";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const DEFAULT_COUNTRY_CODE = "1";
const DEFAULT_VERIFICATION_ERROR_MESSAGE = "Verification code is incorrect. Please try again.";

type OtpApiErrorResponse = {
  error?: string;
  message?: string;
};

const toDigitsOnlyPhoneNumber = (value: string): string => {
  return value.replace(/\D/g, "");
};

const formatPhoneNumber = (input: string): string => {
  const digits = toDigitsOnlyPhoneNumber(input).slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isCompletePhoneNumber = (phoneNumber: string): boolean => {
  return toDigitsOnlyPhoneNumber(phoneNumber).length === 10;
};

const getVerificationErrorMessage = (error: unknown): string => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return DEFAULT_VERIFICATION_ERROR_MESSAGE;
  }

  const response = (error as { response?: { data?: OtpApiErrorResponse | string } }).response;
  const responseData = response?.data;

  if (typeof responseData === "string" && responseData.trim().length > 0) {
    return responseData;
  }

  if (typeof responseData === "object" && responseData !== null) {
    const { error: responseError, message } = responseData;

    if (typeof responseError === "string" && responseError.trim().length > 0) {
      return responseError;
    }

    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  return DEFAULT_VERIFICATION_ERROR_MESSAGE;
};

type UseOtpUiStateParams = {
  smsConsent: boolean;
  onSmsConsentChange: (nextValue: boolean) => void;
  smsPhoneNumber: string;
  smsCountryCode?: string | null;
  initialPhoneVerified?: boolean;
  onSmsPhoneNumberChange: (value: string) => void;
};

export const useOtpUiState = ({
  smsConsent,
  onSmsConsentChange,
  smsPhoneNumber,
  smsCountryCode,
  initialPhoneVerified,
  onSmsPhoneNumberChange,
}: UseOtpUiStateParams) => {
  const [codeSent, setCodeSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(Boolean(initialPhoneVerified));
  const [verifiedPhoneDigits, setVerifiedPhoneDigits] = useState<string | null>(null);
  const [otpValue, setOtpValue] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [otpInputIsFocused, setOtpInputIsFocused] = useState(false);
  const otpInputRef = useRef<TextInput>(null);
  const resolvedSmsCountryCode = smsCountryCode?.replace(/\D/g, "") || DEFAULT_COUNTRY_CODE;

  useEffect(() => {
    if (!initialPhoneVerified) {
      return;
    }

    setPhoneVerified(true);

    const currentPhoneDigits = toDigitsOnlyPhoneNumber(smsPhoneNumber);
    if (currentPhoneDigits.length === 10) {
      setVerifiedPhoneDigits(currentPhoneDigits);
    }
  }, [initialPhoneVerified, smsPhoneNumber]);

  const resetOtpState = useCallback(() => {
    setCodeSent(false);
    setPhoneVerified(false);
    setVerifiedPhoneDigits(null);
    setOtpValue("");
    setSendingCode(false);
    setVerifying(false);
    setOtpErrorMessage(null);
    setCountdown(0);
    setOtpInputIsFocused(false);
    onSmsConsentChange(false);
  }, [onSmsConsentChange]);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setCountdown((previous) => Math.max(previous - 1, 0));
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [countdown]);

  useEffect(() => {
    if (!codeSent || phoneVerified || otpValue.length !== OTP_LENGTH || sendingCode || verifying) {
      return;
    }

    const verifyOtpCode = async () => {
      const currentPhoneDigits = toDigitsOnlyApiPhoneNumber(smsPhoneNumber);
      if (currentPhoneDigits.length !== 10) {
        return;
      }

      setVerifying(true);
      setOtpErrorMessage(null);

      try {
        await verifyReminderSmsVerificationCode({
          countryCode: resolvedSmsCountryCode,
          phoneNumber: currentPhoneDigits,
          otp: otpValue,
        });

        setPhoneVerified(true);
        setVerifiedPhoneDigits(currentPhoneDigits);
        setCodeSent(false);
        setOtpValue("");
        setVerifying(false);
        setOtpErrorMessage(null);
        onSmsConsentChange(false);
        setCountdown(0);
        setOtpInputIsFocused(false);
      } catch (error) {
        setVerifying(false);
        setOtpValue("");
        setOtpErrorMessage(getVerificationErrorMessage(error));
        setOtpInputIsFocused(true);
      }
    };

    void verifyOtpCode();
  }, [
    codeSent,
    onSmsConsentChange,
    otpValue,
    phoneVerified,
    resolvedSmsCountryCode,
    sendingCode,
    smsPhoneNumber,
    verifying,
  ]);

  const handleSmsPhoneNumberChange = useCallback(
    (value: string) => {
      const formattedPhoneNumber = formatPhoneNumber(value);
      const nextPhoneDigits = toDigitsOnlyPhoneNumber(formattedPhoneNumber);
      const currentPhoneDigits = toDigitsOnlyPhoneNumber(smsPhoneNumber);
      const phoneNumberChanged = nextPhoneDigits !== currentPhoneDigits;

      onSmsPhoneNumberChange(formattedPhoneNumber);

      if (!phoneNumberChanged) {
        return;
      }

      const matchesVerifiedPhone = verifiedPhoneDigits !== null && nextPhoneDigits === verifiedPhoneDigits;
      const shouldResetOtpState =
        phoneVerified || codeSent || otpValue.length > 0 || sendingCode || verifying || countdown > 0 || smsConsent;

      if (shouldResetOtpState && !matchesVerifiedPhone) {
        resetOtpState();
      }
    },
    [
      codeSent,
      countdown,
      smsConsent,
      onSmsPhoneNumberChange,
      otpValue.length,
      phoneVerified,
      resetOtpState,
      smsPhoneNumber,
      sendingCode,
      verifying,
      verifiedPhoneDigits,
    ]
  );

  const setSmsConsent = useCallback(
    (nextValue: boolean) => {
      if (!phoneVerified) {
        onSmsConsentChange(false);
        return;
      }

      onSmsConsentChange(nextValue);
    },
    [onSmsConsentChange, phoneVerified]
  );

  const focusOtpInput = useCallback(() => {
    if (!verifying) {
      otpInputRef.current?.focus();
    }
  }, [verifying]);

  const handleSendCodePress = useCallback(() => {
    const requestOtpCode = async () => {
      const currentPhoneDigits = toDigitsOnlyApiPhoneNumber(smsPhoneNumber);
      if (currentPhoneDigits.length !== 10) {
        return;
      }

      if (sendingCode || verifying) {
        return;
      }

      setSendingCode(true);
      setOtpErrorMessage(null);

      try {
        await sendReminderSmsVerificationCode({
          countryCode: resolvedSmsCountryCode,
          phoneNumber: currentPhoneDigits,
        });

        setCodeSent(true);
        setCountdown(RESEND_SECONDS);
        setOtpValue("");
        setVerifying(false);
        setOtpInputIsFocused(true);
      } catch {
        // Keep UI state stable and allow retry on send failures.
      } finally {
        setSendingCode(false);
      }
    };

    void requestOtpCode();
  }, [resolvedSmsCountryCode, sendingCode, smsPhoneNumber, verifying]);

  const handleResendPress = useCallback(() => {
    const canResendAfterFailedVerification = Boolean(otpErrorMessage);

    if ((countdown > 0 && !canResendAfterFailedVerification) || sendingCode || verifying || !codeSent) {
      return;
    }

    handleSendCodePress();
    focusOtpInput();
  }, [codeSent, countdown, focusOtpInput, handleSendCodePress, otpErrorMessage, sendingCode, verifying]);

  const handleOtpChange = useCallback((value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtpErrorMessage(null);
    setOtpValue(nextValue);
  }, []);

  const showOtpSection = codeSent && !phoneVerified;
  const showConsentSection = phoneVerified;
  const showSendCodeAction =
    smsPhoneNumber.length > 0 && !codeSent && !phoneVerified && isCompletePhoneNumber(smsPhoneNumber);
  const activeOtpDigitIndex = otpValue.length >= OTP_LENGTH ? OTP_LENGTH - 1 : otpValue.length;
  const otpDigitGroups = useMemo(() => {
    return [
      [0, 1, 2],
      [3, 4, 5],
    ];
  }, []);

  return {
    OTP_LENGTH,
    codeSent,
    phoneVerified,
    otpValue,
    sendingCode,
    verifying,
    otpErrorMessage,
    countdown,
    otpInputIsFocused,
    smsConsent,
    showOtpSection,
    showConsentSection,
    showSendCodeAction,
    activeOtpDigitIndex,
    otpDigitGroups,
    otpInputRef,
    setSmsConsent,
    setOtpInputIsFocused,
    handleOtpChange,
    handleSendCodePress,
    handleResendPress,
    handleSmsPhoneNumberChange,
    focusOtpInput,
    resetOtpState,
  };
};
